import { Asterisk, Fingerprint, Tag } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import type { PropertySchema } from '@/services/api/schemas'

/**
 * Compact "field ledger" for a proposed type: one row per property with its
 * name, storage type, and the three flags an analyst cares about (required,
 * unique, display name). Reads top-to-bottom like a struct definition.
 */
export function ProposalPropertyList({ properties }: { properties: PropertySchema[] }) {
  const { t } = useTranslation()
  if (properties.length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic">{t('schemaAgent.editor.noProperties')}</p>
    )
  }
  return (
    <ul className="divide-y divide-border rounded-md border border-border bg-muted/20">
      {properties.map(prop => (
        <li key={prop.name} className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 px-3 py-2">
          <div className="flex min-w-0 items-center gap-1.5">
            <code className="truncate font-mono text-sm text-foreground">{prop.name}</code>
            <PropertyMarks prop={prop} />
          </div>
          <Badge variant="secondary" className="h-5 font-mono text-[11px] text-muted-foreground">
            {prop.type}
          </Badge>
          {prop.description && (
            <p className="col-span-2 text-xs leading-snug text-muted-foreground">
              {prop.description}
            </p>
          )}
        </li>
      ))}
    </ul>
  )
}

function PropertyMarks({ prop }: { prop: PropertySchema }) {
  const { t } = useTranslation()
  return (
    <span className="flex shrink-0 items-center gap-1">
      {prop.required && (
        <Asterisk
          className="h-3.5 w-3.5 text-destructive"
          aria-label={t('schemaAgent.workbench.property.required')}
        />
      )}
      {prop.unique && (
        <Fingerprint
          className="h-3.5 w-3.5 text-warning-foreground"
          aria-label={t('schemaAgent.workbench.property.unique')}
        />
      )}
      {prop.is_display_name && (
        <Tag
          className="h-3.5 w-3.5 text-info-foreground"
          aria-label={t('schemaAgent.workbench.property.displayName')}
        />
      )}
    </span>
  )
}

/** Legend for the marks above — rendered once under the ledger. */
export function ProposalPropertyLegend() {
  const { t } = useTranslation()
  const items = [
    {
      icon: Asterisk,
      tone: 'text-destructive',
      label: t('schemaAgent.workbench.property.required'),
    },
    {
      icon: Fingerprint,
      tone: 'text-warning-foreground',
      label: t('schemaAgent.workbench.property.unique'),
    },
    {
      icon: Tag,
      tone: 'text-info-foreground',
      label: t('schemaAgent.workbench.property.displayName'),
    },
  ]
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
      {items.map(({ icon: Icon, tone, label }) => (
        <span key={label} className="inline-flex items-center gap-1">
          <Icon className={`h-3 w-3 ${tone}`} aria-hidden />
          {label}
        </span>
      ))}
    </div>
  )
}
